import { useState, useEffect } from "react";
// Neutral grey placeholder (no running-man, no dead remote service).
import { GREY_PLACEHOLDER as defaultImage } from "../../constants/imageFallback";
import PlayerHeroBanner from "../common/PlayerHeroBanner";
import { formatGroupedNumber } from "../../utils/currencyFormatter";
import AddAgentModal from "./AddAgentModal";
import { useNavigate } from "react-router-dom";
import { playerService, userService } from "../../services/api";
import toast from "react-hot-toast";

// Modal component for multiple invites
const MultipleInvitesModal = ({ invites, onClose, onInviteAction }) => {
  const [responding, setResponding] = useState({});

  const handleResponse = async (inviteId, action) => {
    setResponding((prev) => ({ ...prev, [inviteId]: true }));

    try {
      const response = await playerService.respondToInvite(inviteId, action);

      if (response.success) {
        const message =
          action === "accept"
            ? "Representation invitation accepted successfully!"
            : "Representation invitation declined.";
        toast.success(message);
        onInviteAction(inviteId, action);
      } else {
        toast.error(response.error || `Failed to ${action} invitation`);
      }
    } catch (error) {
      console.error(`Error ${action}ing invitation:`, error);
      toast.error(`Failed to ${action} invitation. Please try again.`);
    } finally {
      setResponding((prev) => ({ ...prev, [inviteId]: false }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Pending Agent Invitations
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {invites.map((invite, index) => (
            <div
              key={invite.id}
              className="flex items-center justify-between px-6 py-3 border border-[#F7D9A4] bg-[#FFF8EC] rounded-xl shadow-lg"
              style={{
                width: "100%",
                height: "64px",
                boxShadow: "0px 4px 16px rgba(16, 11, 39, 0.08)",
              }}
            >
              <div className="flex items-center gap-2">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    width="24"
                    height="24"
                    rx="6"
                    fill="url(#paint0_linear_2130_56168)"
                  />
                  <path
                    d="M12 9V14"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <circle cx="12" cy="17" r="1" fill="white" />
                  <defs>
                    <linearGradient
                      id="paint0_linear_2130_56168"
                      x1="12"
                      y1="0"
                      x2="12"
                      y2="24"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#DBA948" />
                      <stop offset="0.0001" stopColor="#FFC46B" />
                      <stop offset="1" stopColor="#FFA318" />
                    </linearGradient>
                  </defs>
                </svg>

                <span className="text-sm font-medium">
                  You have received a new request from agent "
                  {invite.sender_name || "Unknown Agent"}" to represent you.
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleResponse(invite.id, "accept")}
                  disabled={responding[invite.id]}
                  className="bg-[#FFB039] px-4 py-2 rounded-xl flex items-center gap-2 cursor-pointer transition-colors disabled:opacity-50"
                >
                  <span className="text-white font-bold">
                    {responding[invite.id] ? "..." : "Accept"}
                  </span>
                </button>
                <button
                  onClick={() => handleResponse(invite.id, "decline")}
                  disabled={responding[invite.id]}
                  className="bg-gray-500 px-4 py-2 rounded-xl flex items-center gap-2 cursor-pointer transition-colors disabled:opacity-50"
                >
                  <span className="text-white font-bold">
                    {responding[invite.id] ? "..." : "Decline"}
                  </span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Modal for pending mandates warning
const PendingMandatesModal = ({ onClose, onConfirm }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            Pending Representation Requests
          </h3>
          <p className="text-gray-600 mb-6">
            You still have pending representation requests. If you disable claim
            representation, all pending requests will be automatically rejected.
            Are you sure you want to continue?
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-600 transition-colors"
            >
              Yes, Disable
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PlayerDashboard = ({ user, kycVerified }) => {
  const [isClaimRepresentation, setIsClaimRepresentation] = useState(false);
  const [showAddAgentModal, setShowAddAgentModal] = useState(false);
  const [showMultipleInvitesModal, setShowMultipleInvitesModal] =
    useState(false);
  const [showPendingMandatesModal, setShowPendingMandatesModal] =
    useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [playerProfileData, setPlayerProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggleLoading, setToggleLoading] = useState(false);
  const navigate = useNavigate();
  const [is_active_for_agent, setIsActiveForAgent] = useState(false);
  const [clubInterests, setClubInterests] = useState([]);
  const [selfProfile, setSelfProfile] = useState(null);

  // Fetch pending invites and player profile
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // Fetch pending invites
        const invitesResponse = await playerService.getPendingInvites();
        if (invitesResponse.success) {
          setPendingInvites(invitesResponse.data || []);
        }

        // Fetch clubs that have pitched to the player
        const clubInterestsResponse = await playerService.getPitchedClubs();
        if (clubInterestsResponse.success) {
          setClubInterests(clubInterestsResponse.data || []);
        }

        const userprofile = await userService.getPlayerProfile();

        if (userprofile.success) {
          setSelfProfile(userprofile.data);
          // Fetch player profile using wyscout_id
          const wsPlayerId = userprofile?.data?.wyscout_id;
          if (wsPlayerId) {
            const profileResponse = await playerService.getPublicPlayerProfile(
              wsPlayerId
            );
            if (profileResponse.success) {
              console.log("profile response in the home", profileResponse);
              setPlayerProfileData(profileResponse.data);
            }
          }
          setIsClaimRepresentation(
            userprofile?.data?.claim_representation || false
          );
          setIsActiveForAgent(userprofile?.data?.is_active_for_agent || false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleClaimRepresentationToggle = async (newValue) => {
    // If turning off and there are pending invites, show warning modal
    if (!newValue && pendingInvites.length > 0) {
      setShowPendingMandatesModal(true);
      return;
    }

    await updateClaimRepresentation(newValue);
  };

  const updateClaimRepresentation = async (newValue) => {
    setToggleLoading(true);
    try {
      const response = await playerService.toggleClaimRepresentation(newValue);

      if (response.success) {
        setIsClaimRepresentation(newValue);
        toast.success(
          newValue
            ? "Claim representation enabled successfully!"
            : "Claim representation disabled successfully!"
        );
      } else {
        toast.error(response.error || "Failed to update claim representation");
      }
    } catch (error) {
      console.error("Error updating claim representation:", error);
      toast.error("Failed to update claim representation. Please try again.");
    } finally {
      setToggleLoading(false);
    }
  };

  const handleConfirmDisableClaimRepresentation = async () => {
    setShowPendingMandatesModal(false);

    // Reject all pending invites
    const rejectPromises = pendingInvites.map((invite) =>
      playerService.respondToInvite(invite.id, "decline")
    );

    try {
      await Promise.all(rejectPromises);
      setPendingInvites([]);

      // Now disable claim representation
      await updateClaimRepresentation(false);

      toast.success(
        "All pending requests rejected and claim representation disabled."
      );
    } catch (error) {
      console.error("Error rejecting invites:", error);
      toast.error("Failed to reject some requests. Please try again.");
    }
  };

  const handleAddAgentClick = () => {
    setShowAddAgentModal(true);
  };

  const handleCloseAddAgentModal = () => {
    setShowAddAgentModal(false);
  };

  const handleReviewAgentProfile = (invite) => {
    // If KYC is not verified, block navigation and show a toast
    if (!kycVerified) {
      toast.error("Please verify yourself before reviewing.");
      return;
    }

    // Navigate to agent profile with representation request state
    navigate(`/search/agent/${invite.agent_account_id}`, {
      state: { representationRequest: invite },
    });
  };

  const handleInviteAction = (inviteId, action) => {
    // Remove the invite from the list
    setPendingInvites((prev) =>
      prev.filter((invite) => invite.id !== inviteId)
    );

    // If no more invites, close the modal
    if (pendingInvites.length <= 1) {
      setShowMultipleInvitesModal(false);
    }
  };

  // Show stacked notifications only if there are multiple invites
  const shouldShowStackedNotifications = pendingInvites.length > 1;

  return (
    <div className={`mb-8 flex flex-col gap-6 ${kycVerified ? "mt-6" : ""}`}>
      {/* Stacked toasts - only show if multiple invites */}
      {shouldShowStackedNotifications && (
        <div className="relative h-20 mt-32 md:-mt-2">
          {pendingInvites.slice(0, 3).map((invite, index) => (
            <div
              key={invite.id}
              className="absolute flex flex-col items-start md:flex-row md:items-center justify-between p-4 md:px-6 md:py-3 border border-[#F7D9A4] bg-[#FFF8EC] rounded-xl shadow-lg cursor-pointer w-[calc(100%-2rem)] md:w-full"
              style={{
                maxWidth: `calc(1400px - ${index * 24}px)`,
                height: "auto",
                left: `1rem`,
                bottom: `${20 - index * 10}px`,
                zIndex: pendingInvites.length - index,
                opacity: index === 0 ? 1 : index === 1 ? 0.7 : 0.5,
                boxShadow: "0px 4px 16px rgba(16, 11, 39, 0.08)",
              }}
              onClick={() => setShowMultipleInvitesModal(true)}
            >
              <div className="flex items-center gap-2 mb-2 md:mb-0">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    width="24"
                    height="24"
                    rx="6"
                    fill="url(#paint0_linear_2130_56168)"
                  />
                  <path
                    d="M12 9V14"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <circle cx="12" cy="17" r="1" fill="white" />
                  <defs>
                    <linearGradient
                      id="paint0_linear_2130_56168"
                      x1="12"
                      y1="0"
                      x2="12"
                      y2="24"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#DBA948" />
                      <stop offset="0.0001" stopColor="#FFC46B" />
                      <stop offset="1" stopColor="#FFA318" />
                    </linearGradient>
                  </defs>
                </svg>

                <span className="text-sm font-medium">
                  You have received a new request from agent "
                  {invite.sender_name || "Unknown Agent"}" to represent you.
                </span>
              </div>

              <div
                className="bg-[#FFB039] px-4 py-2 rounded-xl flex items-center gap-2 cursor-pointer transition-colors w-full md:w-auto justify-center"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReviewAgentProfile(invite);
                }}
              >
                <span className="text-white font-bold">
                  Review agent profile
                </span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.33398 6.00016H10.6673M10.6673 6.00016L6.00065 1.3335M10.6673 6.00016L6.00065 10.6668"
                    stroke="white"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Single notification for one invite */}
      {pendingInvites.length === 1 && (
        <div className="relative h-auto md:h-20 mt-32 md:-mt-2">
          <div
            className="absolute flex flex-col md:flex-row items-start md:items-center justify-between p-4 md:px-6 md:py-3 border border-[#F7D9A4] bg-[#FFF8EC] rounded-xl shadow-lg w-[calc(100%-2rem)] md:w-full"
            style={{
              maxWidth: "1400px",
              height: "auto",
              left: "1rem",
              bottom: "20px",
              boxShadow: "0px 4px 16px rgba(16, 11, 39, 0.08)",
            }}
          >
            <div className="flex items-center gap-2 mb-2 md:mb-0">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  width="24"
                  height="24"
                  rx="6"
                  fill="url(#paint0_linear_2130_56168)"
                />
                <path
                  d="M12 9V14"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <circle cx="12" cy="17" r="1" fill="white" />
                <defs>
                  <linearGradient
                    id="paint0_linear_2130_56168"
                    x1="12"
                    y1="0"
                    x2="12"
                    y2="24"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#DBA948" />
                    <stop offset="0.0001" stopColor="#FFC46B" />
                    <stop offset="1" stopColor="#FFA318" />
                  </linearGradient>
                </defs>
              </svg>

              <span className="text-sm font-medium">
                You have received a new request from agent "
                {pendingInvites[0]?.sender_name || "Unknown Agent"}" to represent
                you.
              </span>
            </div>

            <button
              className="bg-[#FFB039] px-4 py-2 rounded-xl flex items-center gap-2 cursor-pointer transition-colors w-full md:w-auto justify-center"
              onClick={() => {
                handleReviewAgentProfile(pendingInvites[0] || {});
              }}
            >
              <span className="text-white font-bold">Review agent profile</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.33398 6.00016H10.6673M10.6673 6.00016L6.00065 1.3335M10.6673 6.00016L6.00065 10.6668"
                  stroke="white"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Use player profile data if available, otherwise fall back to user from props */}
      <PlayerHeroBanner
        player={{
          ...(playerProfileData || {}),
          // ensure agent_name is available in banner from self profile
          agent_name:
            (playerProfileData && playerProfileData.agent_name) ||
            (selfProfile && selfProfile.agent_name) ||
            undefined,
        }}
        isOnClickNavigate={true}
        showSimplePositionIfNoDetailed={true}
      />

      <div className="w-full flex flex-col md:flex-row justify-between items-center gap-6 md:gap-0">
        <div className="flex flex-col sm:flex-row items-center gap-4 md:gap-6 w-full md:w-auto">
          <div
            onClick={() => {
              const navigateId =
                playerProfileData?.wyscout_id ||
                playerProfileData?.id ||
                user?.wyscout_id ||
                user?.id;
              if (navigateId) {
                navigate(`/search/player/${navigateId}`);
              }
            }}
            className="border cursor-pointer border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E] w-full sm:w-auto text-center"
          >
            View all details
          </div>

          <button
            className="bg-[#2283FF] cursor-pointer text-[#F5F5F5] px-4 py-2 rounded-lg disabled:cursor-not-allowed disabled:opacity-50 w-full sm:w-auto"
            disabled={is_active_for_agent || isClaimRepresentation}
            onClick={handleAddAgentClick}
          >
            Add your agent
          </button>
        </div>

        {!(
          selfProfile &&
          (selfProfile.agent_account_id || selfProfile.agent_name)
        ) && (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.9974 0.664551C4.3974 0.664551 0.664062 4.39788 0.664062 8.99788C0.664062 13.5979 4.3974 17.3312 8.9974 17.3312C13.5974 17.3312 17.3307 13.5979 17.3307 8.99788C17.3307 4.39788 13.5974 0.664551 8.9974 0.664551ZM9.83073 13.1646H8.16406V8.16455H9.83073V13.1646ZM9.83073 6.49788H8.16406V4.83122H9.83073V6.49788Z"
                  fill="#646B7E"
                />
              </svg>
              <span className="text-[#292C33] text-lg md:text-xl font-medium text-center">
                Claim representation
              </span>
            </div>
            <div>
              <button
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  isClaimRepresentation ? "bg-blue-600" : "bg-gray-500"
                } ${toggleLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() =>
                  handleClaimRepresentationToggle(!isClaimRepresentation)
                }
                disabled={toggleLoading}
              >
                <span
                  className={`${
                    isClaimRepresentation ? "translate-x-6" : "translate-x-1"
                  } inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out shadow-lg`}
                />
              </button>
            </div>
          </div>
        )}
      </div>

      {clubInterests.length > 0 && (
        <>
          <div className="mt-2">
            <h1 className="text-xl font-bold">Clubs interested in me</h1>
          </div>

          <div className="overflow-x-auto rounded-xl border border-[#D6DBEA] md:hidden">
            {clubInterests.map((club, index) => (
              <div
                key={index}
                className="p-4 border-b bg-white last:border-b-0"
              >
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-2">
                    <img
                      src={club.club_image || defaultImage}
                      alt={club.club_name}
                      className="w-6 h-6 rounded-full"
                    />
                    <span className="text-[#242426] text-base font-medium">
                      {club.club_name}
                    </span>
                  </div>
                  <span
                    className={`px-4 py-1 rounded-lg text-sm font-medium ${
                      club.status === "accepted"
                        ? "bg-[#008D621A] text-[#049669]"
                        : club.status === "pitch_sent"
                        ? "bg-[#FF950033] text-[#AD6501]"
                        : "bg-[#FFEAEE] text-[#FF2D55]"
                    }`}
                  >
                    {club.status === "pitch_sent"
                      ? "Pitched"
                      : club.status.charAt(0).toUpperCase() +
                        club.status.slice(1)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">TRANSFER FEE</p>
                    <span className="text-[#000000] text-sm font-medium">
                      {club.transfer_fee
                        ? `€${formatGroupedNumber(club.transfer_fee)}`
                        : "N/A"}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">REQUEST TYPE</p>
                    <span className="bg-[#D6DBEA] text-[#1A212F] text-sm font-medium px-3 py-1 rounded-full">
                      {club.tender_type}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto rounded-xl border border-[#D6DBEA]  md:block">
            <table className="w-full table-sticky-first">
              <thead className="bg-[#F7F7F7]">
                <tr>
                  <th className="px-6 py-3 text-left text-[#222A35] text-sm font-medium">
                    PITCHED TO CLUB
                  </th>
                  <th className="px-6 py-3 text-left text-[#222A35] text-sm font-medium">
                    TRANSFER FEE
                  </th>
                  <th className="px-6 py-3 text-left text-[#222A35] text-sm font-medium">
                    Request TYPE
                  </th>
                  <th className="px-6 py-3 text-left text-[#222A35] text-sm font-medium">
                    STATUS
                  </th>
                </tr>
              </thead>
              <tbody>
                {clubInterests.map((club, index) => (
                  <tr key={index} className="">
                    <td className="px-6 py-6">
                      <div className="flex items-center gap-2">
                        <img
                          src={club.club_image || defaultImage}
                          alt={club.club_name}
                          className="w-6 h-6 rounded-full"
                        />
                        <span className="text-[#242426] text-base font-medium">
                          {club.club_name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-6">
                      <span className="text-[#000000] text-base font-medium">
                        {club.transfer_fee
                          ? `€${formatGroupedNumber(club.transfer_fee)}`
                          : "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-6">
                      <span className="bg-[#D6DBEA] text-[#1A212F] text-base font-medium px-4 py-1 rounded-full">
                        {club.tender_type}
                      </span>
                    </td>
                    <td className="px-6 py-6">
                      <span
                        className={`px-5 py-1 rounded-lg text-base font-medium ${
                          club.status === "accepted"
                            ? "bg-[#008D621A] text-[#049669]"
                            : club.status === "pitch_sent"
                            ? "bg-[#FF950033] text-[#AD6501]"
                            : "bg-[#FFEAEE] text-[#FF2D55]"
                        }`}
                      >
                        {club.status === "pitch_sent"
                          ? "Pitched"
                          : club.status.charAt(0).toUpperCase() +
                            club.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Add Agent Modal */}
      {showAddAgentModal && (
        <AddAgentModal onClose={handleCloseAddAgentModal} />
      )}

      {/* Multiple Invites Modal */}
      {showMultipleInvitesModal && (
        <MultipleInvitesModal
          invites={pendingInvites}
          onClose={() => setShowMultipleInvitesModal(false)}
          onInviteAction={handleInviteAction}
        />
      )}

      {/* Pending Mandates Modal */}
      {showPendingMandatesModal && (
        <PendingMandatesModal
          onClose={() => setShowPendingMandatesModal(false)}
          onConfirm={handleConfirmDisableClaimRepresentation}
        />
      )}
    </div>
  );
};

export default PlayerDashboard;
